import React, { useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Grid, TextField } from '@mui/material';
import { Refresh, Search } from "@mui/icons-material";
import Button from "@mui/material/Button";
import { toast } from 'react-toastify';
import { InvalidMessage } from "../../Utility/Constants";
import Table from '../../Table/Table';
import { GroupByYearAllColumns } from '../../Table/TableColumn';
import { tableColumns } from '../../Redux/TableSlice';
import { currentData, clearData, updateSearch, searchClicked, updateInputError } from '../../Redux/MNR/GroupByYearAllSlice';
import { clearAll, searchTriggered } from '../../Redux/CNS/CommonSlice';
import {
    validateRegexWithNumbers//, validateRegexWithoutNumbers
} from '../../Validation/Validation';
import { GeneralError } from '../../Utility/Constants';
import Spinner from '../../Spinner/Spinner';
import { showAll, isMNR } from '../../Redux/MNR/MNRCommonSlice';

function GroupByYearAll() {
    const dispatch = useDispatch();
    const error = useSelector(state => state.groupByYearAll.error);
    const isSearched = useSelector(state => state.groupByYearAll.isSearchClicked);
    const searchTriggeredValue = useSelector(state => state.cnsCommon.searchTriggered);
    const inputError = useSelector(state => state.groupByYearAll.inputError);
    const hasAnyError = Object.values(inputError).some(value => value === true);
    const data = useSelector(state => state.groupByYearAll.data);
    const isSearchEnable = ((data.Carrier && data.Carrier.trim() !== '') ||
        (data.Account && data.Account.trim() !== '') || (data.Group && data.Group.trim() !== '') ||
        (data.PlanCode5 && data.PlanCode5.trim() !== ''));
    const loading = useSelector(state => state.groupByYearAll.loading);
    const showAllValue = useSelector(state => state.mnrCommon.showAll);

    useEffect(() => {
        if (error) {
            toast.error(GeneralError);
            return;
        }
    }, [error]);

    const handleResetInitial = (useCallback(() => {
        dispatch(clearAll());
        dispatch(clearData());
        dispatch(updateSearch(true));
        dispatch(searchClicked(false));
        dispatch(showAll(false));
        dispatch(isMNR(true));
    }, [dispatch]))

    const handleReset = () => {
        dispatch(clearData());
        dispatch(updateSearch(true));
        dispatch(searchClicked(false));
        dispatch(showAll(false));
    }

    useEffect(() => {
        handleResetInitial();
        dispatch(tableColumns(GroupByYearAllColumns));
    }, [dispatch, handleResetInitial])

    const handleChange = (name, value) => {
        const val = value.target.value || value.target.innerText;
        switch (name) {
            case 'Account':
                const updateRegexWithoutNumbersError = validateRegexWithNumbers(name, val, inputError);
                dispatch(updateInputError(updateRegexWithoutNumbersError));
                break;
            case 'Group':
                const updateRegexWithNumbers = validateRegexWithNumbers(name, val, inputError);
                dispatch(updateInputError(updateRegexWithNumbers));
                break;
            case 'PlanCode5':
                const updateRegexWithNumbersToo = validateRegexWithNumbers(name, val, inputError);
                dispatch(updateInputError(updateRegexWithNumbersToo));
                break;
            case 'Carrier':
                const updateRegexWithAlphabets = validateRegexWithNumbers(name, val, inputError);
                dispatch(updateInputError(updateRegexWithAlphabets));
                break;
            default:
                break;
        }

        const updatedData = {
            ...data,
            [name]: val
        };
        dispatch(currentData(updatedData));
    }

    const handleSearch = () => {
        dispatch(showAll(false));
        dispatch(searchClicked(true));
        dispatch(searchTriggered(!searchTriggeredValue));
    }

    const handleShowAll = () => {
        dispatch(showAll(true));
        dispatch(clearData());
        dispatch(searchClicked(false));
        dispatch(searchTriggered(!searchTriggeredValue));
    }

    return (
        <>
            {loading && <Spinner />}
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 1.714285714285714, lg: 1.714285714285714, xl: 1.714285714285714 }}>
                    <TextField
                        id="carrier"
                        label="Carrier"
                        variant="outlined"
                        size="small"
                        onChange={(e) => handleChange('Carrier', e)}
                        value={data.Carrier}
                        error={inputError.Carrier}
                        helperText={inputError.Carrier ? InvalidMessage : ''}
                        fullWidth
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 1.714285714285714, lg: 1.714285714285714, xl: 1.714285714285714 }}>
                    <TextField
                        id="account"
                        label="Account"
                        variant="outlined"
                        size="small"
                        onChange={(e) => handleChange('Account', e)}
                        value={data.Account}
                        error={inputError.Account}
                        helperText={inputError.Account ? InvalidMessage : ''}
                        fullWidth
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 1.714285714285714, lg: 1.714285714285714, xl: 1.714285714285714 }}>
                    <TextField
                        id="group"
                        label="Group"
                        variant="outlined"
                        size="small"
                        onChange={(e) => handleChange('Group', e)}
                        value={data.Group}
                        error={inputError.Group}
                        helperText={inputError.Group ? InvalidMessage : ''}
                        fullWidth
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 1.714285714285714, lg: 1.714285714285714, xl: 1.714285714285714 }}>
                    <TextField
                        id="planCode5"
                        label="PlanCode5"
                        variant="outlined"
                        size="small"
                        onChange={(e) => handleChange('PlanCode5', e)}
                        value={data.PlanCode5}
                        error={inputError.PlanCode5}
                        helperText={inputError.PlanCode5 ? InvalidMessage : ''}
                        fullWidth
                    />
                </Grid>
            </Grid>
            <Grid container spacing={3} sx={{ justifyContent: 'center', paddingTop: '24px' }}>
                <Button
                    variant="contained"
                    onClick={handleShowAll}
                >
                    Show All
                </Button>
                <Button
                    variant={(!isSearched && !showAllValue) ? "contained" : "outlined"}
                    style={isSearched ? { color: 'var(--primary-font-color)' } : undefined}
                    size="small"
                    onClick={handleReset}
                    endIcon={<Refresh />}
                    disabled={!isSearched && !showAllValue}
                >
                    Reset
                </Button>
                <Button
                    variant={(!isSearchEnable || hasAnyError) ? "contained" : "contained"}
                    size="large"
                    onClick={handleSearch}
                    endIcon={<Search />}
                    style={(isSearchEnable && !hasAnyError) ? { color: 'var(--primary-font-color)', background: 'var(--primary-color)', border: '1px solid var(--primary-font-color)' } : undefined}
                    disabled={!isSearchEnable || hasAnyError}
                >
                    Search
                </Button>
            </Grid>
            {((isSearched) || (showAllValue)) && <Table />}
        </>
    )
}

export default GroupByYearAll;